import {
    Accordion,
    FlexyButton,
    ItemActions,
    UploaderProvider,
    useModal,
} from '@europrocurement/flexy-components';
import type {
    Acompte,
    Collection,
} from '@europrocurement/l2d-modules/providers/CollectionBillingProvider';
import { Box, Card, PaletteColor, Typography, useTheme } from '@mui/material';
import * as React from 'react';
import { euroIcon, lockIcon, sendIcon } from '@europrocurement/l2d-icons';
import { useCollectionBillingCtx } from '../../../../../../../providers/CollectionBillingProvider';
import ProvisionAttendue from '../../../ProvisionAttendue/ProvisionAttendue';
import ProvisionAdd from '../../../ProvisionAdd/ProvisionAdd';
import useDossierFacturationActions from '../../hooks/useDossierFacturationActions';
import { useCurrentDossierCtx } from '../../../../../providers/CurrentDossierProvider';
import usePayActions from '../../hooks/usePayActions';
import type { DocumentGed } from '../../../../../hooks';

type ProvisionsSectionProps = {
    acompteAttendu: number;
    totalAcomptes: number;
    totalReglements: number;
    collections: Collection[];
    hasActiveInvoice: boolean;
    onAcompteCreated?: (uploadedDocs: DocumentGed[]) => void;
};

const ProvisionsSection: React.FunctionComponent<ProvisionsSectionProps> = function ({
    acompteAttendu,
    totalAcomptes,
    totalReglements,
    collections,
    hasActiveInvoice,
    onAcompteCreated,
}: ProvisionsSectionProps) {
    const { modalActions } = useModal();
    const { palette } = useTheme();
    const { updateProvisionAttendue, createAcompte } = useDossierFacturationActions();
    const { dossier } = useCurrentDossierCtx();
    const { getRecipientId } = useCollectionBillingCtx();
    const { handleSendAcomptePaymentMail, handlePayInvoices } = usePayActions();

    const [isLoading, setIsLoading] = React.useState(false);

    const wrapperInvoicePaymentSx = {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    };

    const labelSx = {
        fontSize: '15px',
        fontWeight: '600',
        lineHeight: '21px',
    };

    const amountLabelSx = {
        fontSize: '22px',
        fontWeight: '600',
        lineHeight: '26.4px',
    };

    const amount = acompteAttendu - (totalAcomptes + totalReglements);

    const handleUpdateProvisionAttendue = async (provisionAttendue: number) => {
        const collection = collections[0];

        if (!collection) {
            return;
        }

        modalActions.close();
        updateProvisionAttendue(collection, provisionAttendue);
    };

    const handleCreateAcompte = async (acompte: Acompte, files?: File[]) => {
        const collection = collections[0];

        if (!collection) {
            return;
        }

        modalActions.close();
        const result = await createAcompte(collection, acompte, files, dossier);
        onAcompteCreated?.(result.uploadedDocs);
    };

    const onClickDemandeReglement = async () => {
        setIsLoading(true);
        try {
            const recipientId = getRecipientId();
            const prescripteurId = dossier?.prescripteurId as number;
            await handleSendAcomptePaymentMail(
                recipientId as number,
                prescripteurId,
                collections,
                totalAcomptes,
                dossier?.id ? [dossier.id] : [],
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card
            id="provisions"
            sx={{ boxShadow: 'none', padding: 0, mx: 0 }}
        >
            {/* <JsonView data={collections} /> */}
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Demande de provision"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Provision (TTC) attendue :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.primary' }}
                            >
                                {(
                                    Math.floor((acompteAttendu > 0 ? acompteAttendu : 0) * 100) /
                                    100
                                ).toFixed(2)}
                                &euro;
                            </Typography>
                            <ItemActions
                                onEdit={
                                    collections.length === 1
                                        ? (event) => {
                                              event.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                                              modalActions.call(
                                                  <ProvisionAttendue
                                                      provision={
                                                          collections[0]?.acompteAttendu ?? 0
                                                      }
                                                      onUpdate={handleUpdateProvisionAttendue}
                                                  />,
                                              );
                                          }
                                        : undefined
                                }
                                sx={{
                                    pl: 1,
                                }}
                            />
                        </Box>
                    </Box>
                }
                footerActions={[
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'end',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        {amount > 0 && collections.length === 1 && !hasActiveInvoice ? (
                            <FlexyButton
                                label="Encaisser la provision à régler"
                                icon={euroIcon}
                                variant="outlined"
                                color={palette.primary}
                                onClick={() => {
                                    modalActions.call(
                                        <UploaderProvider>
                                            <ProvisionAdd
                                                onAddProvision={handleCreateAcompte}
                                                collectionId={collections[0].id as number}
                                                overwriteDefaultProvisionAdd={{
                                                    montant: (
                                                        (collections[0].acompteAttendu ?? 0) -
                                                        (totalAcomptes + totalReglements)
                                                    )
                                                        .toFixed(2)
                                                        .toString(),
                                                }}
                                            />
                                        </UploaderProvider>,
                                        true,
                                        false,
                                    );
                                }}
                            />
                        ) : null}
                        {amount > 0 ? (
                            <FlexyButton
                                label="Demander le règlement de la provision"
                                icon={sendIcon}
                                variant="outlined"
                                color={palette.primary}
                                onClick={onClickDemandeReglement}
                                loading={isLoading}
                            />
                        ) : null}
                        {amount > 0 && collections.length === 1 ? (
                            <FlexyButton
                                label="Régler la provision"
                                icon={lockIcon}
                                variant="contained"
                                color={palette.primary}
                                onClick={() =>
                                    collections[0].id !== undefined &&
                                    handlePayInvoices(
                                        dossier?.collectionNumero as string,
                                        (collections[0].acompteAttendu ?? 0) - totalAcomptes,
                                        [collections[0].id as number],
                                        'collection',
                                    )
                                }
                            />
                        ) : null}
                    </Box>,
                ]}
            >
                <Box sx={wrapperInvoicePaymentSx}>
                    <Typography sx={labelSx}>Provision (TTC) à régler</Typography>
                    <Typography sx={amountLabelSx}>
                        {amount > 0 ? amount.toFixed(2) : '0.00'}&euro;
                    </Typography>
                </Box>
            </Accordion>
        </Card>
    );
};

export default ProvisionsSection;
